// @flow

import React, { PureComponent, type Node, type ElementRef } from 'react';
import AvatarHoverCardBackground from 'app/common/react/components/AvatarHoverCard/AvatarHoverCardBackground';
import AvatarHoverCardContent from 'app/common/react/components/AvatarHoverCard/AvatarHoverCardContent';
import AvatarHoverCardFlyout from 'app/common/react/components/AvatarHoverCard/AvatarHoverCardFlyout';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import StopEventPropagation from 'app/packages/stop-event-propagation/StopEventPropagation';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { GridPin } from 'app/packages/pinterest-flow-types/pin';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { ShowcaseBoardType } from 'app/common/react/components/profile/showcase/commonTypes';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { MODAL_ZINDEX } from 'app/common/react/ui/Modal';
import { MultiTreatmentExp } from 'app/common/lib/experiment';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Layer } from 'gestalt';

export type HOCProps = {|
  requestContext: RequestContextType,
  viewer: AuthViewer,
  viewType?: $Values<PCONST.thrift.ViewType>,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
|};

export type OwnProps = {|
  avatarSize: number,
  children: Node,
  isDarkPromoter?: boolean,
  pinId: string,
  user: ?ProfileUser,
  username: ?string,
|};

type Props = {|
  ...HOCProps,
  ...OwnProps,
|};

export type State = {|
  currentPinId: ?string,
  detailedUser: ?ProfileUser,
  isHovering: boolean,
  isLoaded: boolean,
  isMounted: boolean,
  isVisible: boolean,
  showcase: ?Array<ShowcaseBoardType>,
  user: ?ProfileUser,
  userPins: ?Array<GridPin>,
|};

const OPEN_DELAY_TIME_MS = 300;
const CLOSE_DELAY_TIME_MS = 150;

class AvatarHoverCard extends PureComponent<Props, State> {
  anchorRef: ElementRef<typeof Box> = React.createRef();
  state: State;
  openTimeout: TimeoutID;
  dismissTimeout: TimeoutID;

  constructor(props: Props) {
    super(props);
    this.state = {
      currentPinId: props.pinId,
      detailedUser: null,
      isHovering: false,
      isMounted: false,
      isLoaded: false,
      isVisible: false,
      showcase: null,
      user: null,
      userPins: null,
    };
  }

  loadUser = () => {
    const { username } = this.props;
    const { isMounted } = this.state;

    if (!username) {
      return;
    }

    ResourceFactory.create('UserResource', {
      username,
      field_set_key: 'profile',
    })
      .callGet()
      .then((response) => {
        if (isMounted) {
          this.setState({
            detailedUser: response.resource_response.data,
          });
        }
      });

    ResourceFactory.create('UserPinsResource', {
      username,
      page_size: 3,
      field_set_key: 'grid_item',
      isPrefetch: false,
    })
      .callGet()
      .then((response) => {
        if (isMounted) {
          this.setState({
            userPins: response.resource_response.data,
          });
        }
      });

    ResourceFactory.create('ShowcaseResource', {
      username,
      page_size: 2,
      isPrefetch: false,
    })
      .callGet()
      .then((response) => {
        if (isMounted) {
          this.setState({
            showcase: response.resource_response.data,
          });
        }
      });
    if (isMounted) {
      this.setState({ isLoaded: true });
    }
  };

  handleScroll = () => {
    clearTimeout(this.openTimeout);
    if (this.state.isMounted) {
      this.setState({
        isVisible: false,
      });
      this.forceUpdate();
    }
  };

  componentDidMount() {
    this.setState({
      isMounted: true,
    });
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    this.setState({
      isMounted: false,
    });
    clearTimeout(this.openTimeout);
    clearTimeout(this.dismissTimeout);
    window.removeEventListener('scroll', this.handleScroll);
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { user } = props;
    const { detailedUser } = state;

    return {
      user: {
        ...(Boolean(user) ? user : {}),
        ...(Boolean(detailedUser) ? detailedUser : {}),
      },
    };
  }

  timeoutDismiss = () => {
    this.dismissTimeout = setTimeout(() => {
      if (this.state.isMounted) {
        this.setState({ isHovering: false, isVisible: false });
        clearTimeout(this.openTimeout);
      }
    }, CLOSE_DELAY_TIME_MS);
  };

  handleDisplayCard = () => {
    const { isDarkPromoter, pinId, requestContext, viewParameter } = this.props;
    const { currentPinId, isLoaded, isMounted } = this.state;
    // handles issue of card not re-rendering when moved to a new user before timeout
    clearTimeout(this.openTimeout);
    clearTimeout(this.dismissTimeout);

    if (!!pinId && currentPinId !== pinId) {
      this.forceUpdate();
    }

    if (!!pinId) {
      this.setState({ isHovering: true, currentPinId: pinId });
    }

    const hoverCardExp = new MultiTreatmentExp(requestContext, 'web_avatar_hover_cards_v2');

    if (
      (viewParameter === PCONST.thrift.ViewParameterType.PIN_CLOSEUP ||
        viewParameter === PCONST.thrift.ViewParameterType.FEED_FOLLOWING) &&
      !isDarkPromoter &&
      (hoverCardExp.check('enabled') ||
        hoverCardExp.check('enabled2_vertical') ||
        hoverCardExp.check('enabled2_horizontal'))
    ) {
      if (!isLoaded) {
        this.loadUser();
      }
      this.openTimeout = setTimeout(() => {
        if (isMounted) {
          this.setState({ isVisible: true });
        }
      }, OPEN_DELAY_TIME_MS);
    } else if (isMounted) {
      this.setState({ isVisible: false });
    }
  };

  handleHideCard = () => {
    this.timeoutDismiss();
  };

  render() {
    const {
      avatarSize,
      children,
      requestContext,
      username,
      viewer,
      viewParameter,
      viewType,
    } = this.props;
    const { isVisible, showcase, user, userPins } = this.state;
    const {
      about,
      domain_url: domainUrl,
      follower_count: followerCount,
      full_name: fullName,
      id,
      image_medium_url: imageUrl,
      show_creator_profile: showCreatorProfile,
      verified_identity,
    } = user || {};

    const isViewerProfile = viewer.id === id;
    const isVerified = !!verified_identity && verified_identity.verified;
    const hoverCardExp = new MultiTreatmentExp(requestContext, 'web_avatar_hover_cards_v2');
    const isHorizontalDesign = hoverCardExp.checkWithNoActivation('enabled2_horizontal');

    return (
      <Box
        onMouseOver={this.handleDisplayCard}
        onFocus={this.handleDisplayCard}
        onMouseLeave={this.handleHideCard}
      >
        <Box marginLeft={-2} marginRight={-2} paddingX={2} position="relative">
          <Box
            height={avatarSize}
            left
            marginLeft={2}
            position="absolute"
            ref={this.anchorRef}
            top
            width={avatarSize}
          />
          {children}
        </Box>
        {!isViewerProfile &&
          !!user &&
          !!username &&
          isVisible && (
            <Box>
              <StopEventPropagation>
                <Layer>
                  <AvatarHoverCardFlyout
                    anchor={this.anchorRef.current}
                    isHorizontalDesign={isHorizontalDesign}
                  >
                    <Box
                      color="white"
                      dangerouslySetInlineStyle={{ __style: { zIndex: MODAL_ZINDEX } }}
                      onFocus={this.handleDisplayCard}
                      onMouseOver={this.handleDisplayCard}
                      onMouseLeave={this.handleHideCard}
                      position="relative"
                      width="100%"
                    >
                      <Box width="100%">
                        <AvatarHoverCardBackground
                          isHorizontalDesign={isHorizontalDesign}
                          userPins={userPins}
                          showcase={showcase}
                        />
                        <AvatarHoverCardContent
                          avatarUrl={imageUrl}
                          description={about}
                          domainUrl={domainUrl}
                          followerCount={followerCount}
                          id={id}
                          isCreator={showCreatorProfile}
                          isHorizontalDesign={isHorizontalDesign}
                          isVerified={isVerified}
                          userFullName={fullName}
                          username={username}
                          viewParameter={viewParameter}
                          viewType={viewType}
                        />
                      </Box>
                    </Box>
                  </AvatarHoverCardFlyout>
                </Layer>
              </StopEventPropagation>
            </Box>
          )}
      </Box>
    );
  }
}

export default compose(
  withRequestContext,
  withViewer,
)(AvatarHoverCard);
